import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { invalidateMany } from './queryUtils';
import {
  getTimeOffTypes,
  createTimeOffType,
  updateTimeOffType,
  deleteTimeOffType,
  getUserTimeOffs,
  getAllTimeOffs,
  getTimeOff,
  createTimeOffRequest,
  updateTimeOffRequest,
  submitTimeOff,
  cancelTimeOff,
  adminCancelTimeOff,
  approveTimeOff,
  rejectTimeOff,
  getPendingTimeOffs,
  getTeamTimeOffs,
  bulkApproveTimeOffs,
  bulkRejectTimeOffs,
  bulkSubmitTimeOffs,
  bulkCancelTimeOffs,
  getMyVacationBalance,
  getMyTimeOffAllowances,
  type CreateTimeOffInput,
  type TimeOffTypeInput,
  type UpdateTimeOffInput,
} from '../services/api/timeOff';

/**
 * Invalidate the three time-off list queues every mutation hook needs to
 * refresh — the user's own `['timeOffs']`, the manager pending bucket
 * (`['pendingTimeOffs']`), and the admin-wide list (`['allTimeOffs']`).
 * Extracted in F2-016 follow-up to keep the 11+ mutation hooks below from
 * repeating the same triple. Per-item (`['timeOff', id]`) and
 * `['approvalCounts']` invalidations remain inline at the call sites
 * because they only apply to a subset of mutations.
 */
function invalidateTimeOffLists(queryClient: QueryClient): void {
  invalidateMany(queryClient, [
    ['timeOffs'],
    ['pendingTimeOffs'],
    ['allTimeOffs'],
    // MKS-326: every one of those mutations moves usedHours or pendingHours.
    ['myVacationBalance'],
    // MKS-327: and the same for a type with its own yearly cap in days.
    ['myTimeOffAllowances'],
  ]);
}

/**
 * MKS-327: `includeArchived` is part of the query key, so the admin page's
 * full list and every picker's pickable list are cached separately instead of
 * one overwriting the other.
 */
export function useTimeOffTypes(options?: { includeArchived?: boolean }) {
  const includeArchived = options?.includeArchived ?? false;
  return useQuery({
    queryKey: ['timeOffTypes', { includeArchived }],
    queryFn: () => getTimeOffTypes({ includeArchived }),
  });
}

/**
 * MKS-327 — the signed-in user's remaining allowance for every type with its
 * own yearly cap in days. Empty while the flag is off or no type has a cap.
 *
 * Invalidated with the request lists: submitting or cancelling a request moves
 * `usedDays`/`pendingDays`.
 */
export function useMyTimeOffAllowances(year: number) {
  return useQuery({
    queryKey: ['myTimeOffAllowances', year],
    queryFn: () => getMyTimeOffAllowances(year),
  });
}

/**
 * MKS-326 — the signed-in user's own vacation balance for `year`.
 *
 * Invalidated by `invalidateTimeOffLists` with the request lists: submitting,
 * approving or deleting a request moves `pendingHours`/`usedHours`.
 */
export function useMyVacationBalance(year: number) {
  return useQuery({
    queryKey: ['myVacationBalance', year],
    queryFn: () => getMyVacationBalance(year),
  });
}

export function useCreateTimeOffType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TimeOffTypeInput) => createTimeOffType(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeOffTypes'] });
    },
  });
}

export function useUpdateTimeOffType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: TimeOffTypeInput }) =>
      updateTimeOffType(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeOffTypes'] });
    },
  });
}

export function useDeleteTimeOffType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteTimeOffType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeOffTypes'] });
    },
  });
}

export function useUserTimeOffs(filters?: { year?: number; state?: string }) {
  return useQuery({
    queryKey: ['timeOffs', filters],
    queryFn: () => getUserTimeOffs(filters),
  });
}

export function useTimeOff(id: number | undefined) {
  return useQuery({
    queryKey: ['timeOff', id],
    queryFn: () => getTimeOff(id!),
    enabled: !!id,
  });
}

export function useCreateTimeOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTimeOffInput) => createTimeOffRequest(input),
    // F2-016: also invalidate manager-facing queues so freshly submitted
    // (or auto-submitted) requests appear without a page reload.
    onSuccess: () => {
      invalidateTimeOffLists(queryClient);
    },
  });
}

export function useUpdateTimeOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateTimeOffInput }) =>
      updateTimeOffRequest(id, input),
    // F2-016: invalidate manager-facing queues; an updated request may
    // change date ranges or types that managers see in pending/all lists.
    onSuccess: (_data, { id }) => {
      invalidateTimeOffLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['timeOff', id] });
    },
  });
}

export function useSubmitTimeOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => submitTimeOff(id),
    // F2-016: submitting flips state to pending; managers need the pending
    // and all-time-offs queues invalidated to see the new row immediately.
    // F-208: also invalidate approvalCounts so the cockpit badge updates.
    onSuccess: (_data, id) => {
      invalidateTimeOffLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['timeOff', id] });
      queryClient.invalidateQueries({ queryKey: ['approvalCounts'] });
    },
  });
}

export function useCancelTimeOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cancelTimeOff(id),
    // F-207: cancellation removes an item from the pending queue and changes
    // approval badge count; invalidate all three lists plus approvalCounts.
    onSuccess: (_data, id) => {
      invalidateTimeOffLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['timeOff', id] });
      queryClient.invalidateQueries({ queryKey: ['approvalCounts'] });
    },
  });
}

export function useApproveTimeOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => approveTimeOff(id),
    onSuccess: (_data, id) => {
      invalidateTimeOffLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['timeOff', id] });
      queryClient.invalidateQueries({ queryKey: ['approvalCounts'] });
    },
  });
}

export function useRejectTimeOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => rejectTimeOff(id, reason),
    onSuccess: (_data, { id }) => {
      invalidateTimeOffLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['timeOff', id] });
      queryClient.invalidateQueries({ queryKey: ['approvalCounts'] });
    },
  });
}

export function useTeamTimeOffs() {
  return useQuery({
    queryKey: ['teamTimeOffs'],
    queryFn: getTeamTimeOffs,
  });
}

export function usePendingTimeOffs() {
  return useQuery({
    queryKey: ['pendingTimeOffs'],
    queryFn: getPendingTimeOffs,
  });
}

export function useAllTimeOffs(filters?: { year?: number; state?: string }) {
  return useQuery({
    queryKey: ['allTimeOffs', filters],
    queryFn: () => getAllTimeOffs(filters),
  });
}

export function useAdminCancelTimeOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminCancelTimeOff(id),
    // F-209: admin cancel removes an item from the approval queue;
    // approvalCounts badge must refresh immediately.
    onSuccess: () => {
      invalidateTimeOffLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['approvalCounts'] });
    },
  });
}

// ===== Bulk Operations =====

export function useBulkSubmitTimeOffs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => bulkSubmitTimeOffs(ids),
    onSuccess: () => {
      invalidateTimeOffLists(queryClient);
    },
  });
}

export function useBulkApproveTimeOffs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => bulkApproveTimeOffs(ids),
    onSuccess: () => {
      invalidateTimeOffLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['approvalCounts'] });
    },
  });
}

export function useBulkRejectTimeOffs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, reason }: { ids: number[]; reason?: string }) => bulkRejectTimeOffs(ids, reason),
    onSuccess: () => {
      invalidateTimeOffLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['approvalCounts'] });
    },
  });
}

export function useBulkCancelTimeOffs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => bulkCancelTimeOffs(ids),
    onSuccess: () => {
      invalidateTimeOffLists(queryClient);
    },
  });
}
